import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, AlertTriangle, Check, PlayCircle } from 'lucide-react';
import { ApiClientError, DataTable, EmptyState, Money, Spinner, StatusBadge, type Column } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Settlement engine + UTR reconciliation
 *
 *  Running a batch moves real money, so the trigger is a two-step confirm and
 *  the result is reported per merchant — including failures, which never abort
 *  the rest of the batch. Reconciliation is the second half: treasury pastes
 *  the bank UTR against each settlement, and only then does it become PAID.
 * ========================================================================== */

interface SettlementRow {
  settlement_ref: string;
  batch_id: string;
  cycle: string;
  gross_amount: string;
  net_payout: string;
  reserve_held: string;
  currency: string;
  transaction_count: number;
  status: string;
  utr: string | null;
  scheduled_for: string;
  paid_at: string | null;
  merchant_ref: string;
  display_name: string;
}

interface BatchResult {
  batch_id: string;
  settled_count: number;
  failed_count: number;
  total_net: { amount: string; formatted: string };
  settlements: {
    settlement_id: string;
    merchant_ref: string;
    transactions: number;
    net: { formatted: string };
    reserve_held: { formatted: string };
  }[];
  failures: { merchantId: string; error: string }[];
}

/**
 * Close a settlement out by hand — paid against a bank UTR, or failed.
 *
 * Failing one is not a status change: it reverses the journal and releases the
 * transactions so the next batch settles them again. The merchant is still
 * owed the money, so it goes back to pending rather than evaporating.
 */
function UtrCell({ settlement }: { settlement: SettlementRow }) {
  const queryClient = useQueryClient();
  const [utr, setUtr] = useState('');
  const [failing, setFailing] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const done = () => {
    setError(null);
    setFailing(false);
    setReason('');
    void queryClient.invalidateQueries({ queryKey: ['settlements'] });
  };

  const reconcile = useMutation({
    mutationFn: () => api.post(`/settlements/${settlement.settlement_ref}/reconcile`, { status: 'PAID', utr }),
    onSuccess: done,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Reconciliation failed.'),
  });

  const fail = useMutation({
    mutationFn: () =>
      api.post(`/settlements/${settlement.settlement_ref}/reconcile`, { status: 'FAILED', reason: reason.trim() }),
    onSuccess: done,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not reverse it.'),
  });

  if (settlement.utr) return <span className="numeral text-xs text-content-muted">{settlement.utr}</span>;
  if (settlement.status !== 'PENDING' && settlement.status !== 'PROCESSING') {
    return <span className="text-2xs text-content-faint">—</span>;
  }

  if (failing) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why the bank rejected it"
          className="input h-8 w-48 text-2xs"
          aria-label={`Failure reason for ${settlement.settlement_ref}`}
        />
        <button
          type="button"
          onClick={() => fail.mutate()}
          disabled={reason.trim().length < 10 || fail.isPending}
          className="btn btn-sm h-8 border-danger-ring bg-danger-soft px-2 text-2xs text-danger-text"
        >
          {fail.isPending ? <Spinner className="h-3 w-3" /> : 'Reverse'}
        </button>
        <button type="button" onClick={() => setFailing(false)} className="btn btn-ghost btn-sm h-8 px-1.5 text-2xs">
          Cancel
        </button>
        {error && <span className="text-2xs text-danger">{error}</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        value={utr}
        onChange={(e) => setUtr(e.target.value.toUpperCase())}
        placeholder="Bank UTR"
        className="input numeral h-8 w-36 text-2xs"
        aria-label={`UTR for ${settlement.settlement_ref}`}
      />
      <button
        type="button"
        onClick={() => reconcile.mutate()}
        disabled={utr.trim().length < 6 || reconcile.isPending}
        className="btn btn-secondary btn-sm h-8 px-2 text-2xs"
        title="Mark paid against this UTR"
      >
        {reconcile.isPending ? <Spinner className="h-3 w-3" /> : <Check className="h-3 w-3" />}
      </button>
      <button
        type="button"
        onClick={() => { setFailing(true); setError(null); }}
        className="btn btn-ghost btn-sm h-8 px-1.5 text-2xs text-content-subtle hover:text-danger"
        title="Reverse: return the money to pending and re-settle later"
      >
        <X className="h-3 w-3" />
      </button>
      {error && <span className="text-2xs text-danger">{error}</span>}
    </div>
  );
}

export function SettlementEngine() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [cycle, setCycle] = useState<'T0' | 'T1' | 'T2' | 'T3' | 'T7' | ''>('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['settlements', status],
    queryFn: () => api.get<{ data: SettlementRow[] }>('/settlements', { status: status || undefined }),
    refetchInterval: 60_000,
  });

  const runBatch = useMutation({
    mutationFn: () => api.post<BatchResult>('/settlements/run', cycle ? { cycle } : {}),
    onSuccess: (batch) => {
      setResult(batch);
      setConfirming(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['settlements'] });
    },
    onError: (err) => {
      setConfirming(false);
      setError(err instanceof ApiClientError ? err.message : 'The batch could not be started.');
    },
  });

  const columns: Column<SettlementRow>[] = [
    {
      key: 'ref',
      header: 'Settlement',
      render: (s) => (
        <div>
          <p className="numeral text-xs text-content">{s.settlement_ref}</p>
          <p className="numeral mt-0.5 text-2xs text-content-faint">{s.batch_id}</p>
        </div>
      ),
    },
    {
      key: 'merchant',
      header: 'Merchant',
      render: (s) => (
        <div>
          <p className="text-xs text-content">{s.display_name}</p>
          <p className="numeral mt-0.5 text-2xs text-content-faint">
            {s.merchant_ref} · {s.cycle} · {s.transaction_count} txns
          </p>
        </div>
      ),
    },
    { key: 'gross', header: 'Gross', align: 'right', render: (s) => <Money amount={s.gross_amount} currency={s.currency} className="text-xs" /> },
    {
      key: 'reserve',
      header: 'Reserve',
      align: 'right',
      render: (s) => <Money amount={s.reserve_held} currency={s.currency} className="text-xs text-content-subtle" />,
    },
    {
      key: 'net',
      header: 'Net payout',
      align: 'right',
      render: (s) => <Money amount={s.net_payout} currency={s.currency} className="text-xs font-medium text-brand-400" />,
    },
    { key: 'status', header: 'Status', render: (s) => <StatusBadge status={s.status} /> },
    { key: 'utr', header: 'UTR', render: (s) => <UtrCell settlement={s} /> },
  ];

  return (
    <div className="space-y-4">
      {/* ---- Trigger ---------------------------------------------------- */}
      <div className="panel p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-sm font-medium">Run a settlement batch</h2>
            <p className="mt-1 max-w-prose text-xs text-content-muted">
              Sweeps every approved, unfrozen merchant whose captured volume has aged past its cycle. Each merchant
              settles in its own transaction — one failure never blocks the rest of the batch.
            </p>
          </div>

          <div className="flex items-end gap-2">
            <div>
              <label htmlFor="cycle" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
                Cycle
              </label>
              <select id="cycle" value={cycle} onChange={(e) => setCycle(e.target.value as typeof cycle)} className="input w-32">
                <option value="">All cycles</option>
                {['T0', 'T1', 'T2', 'T3', 'T7'].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {confirming ? (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setConfirming(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="button" onClick={() => runBatch.mutate()} className="btn btn-danger" disabled={runBatch.isPending}>
                  {runBatch.isPending ? <Spinner /> : <AlertTriangle className="h-4 w-4" />}
                  Yes, move the money
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirming(true)} className="btn btn-primary">
                <PlayCircle className="h-4 w-4" />
                Run batch
              </button>
            )}
          </div>
        </div>

        {confirming && (
          <p className="mt-4 flex items-start gap-2 rounded border border-warning-ring bg-warning-soft px-3 py-2.5 text-xs text-[#FCD34D]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            This releases merchant funds and creates payout instructions{cycle ? ` for the ${cycle} cycle` : ' across every cycle'}.
            It is recorded in the audit log against your account and cannot be undone — a mistaken batch has to be
            reversed settlement by settlement.
          </p>
        )}

        {error && (
          <p role="alert" className="mt-4 rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-[#FDA4AF]">
            {error}
          </p>
        )}

        {result && (
          <div className="mt-4 rounded border border-brand-800 bg-success-soft p-3">
            <p className="text-xs text-brand-300">
              Batch <span className="numeral">{result.batch_id}</span> complete — {result.settled_count} settled
              {result.failed_count > 0 && `, ${result.failed_count} failed`} · {result.total_net.formatted} released.
            </p>
            {result.failures.length > 0 && (
              <ul className="mt-2 space-y-1">
                {result.failures.map((failure) => (
                  <li key={failure.merchantId} className="numeral text-2xs text-[#FDA4AF]">
                    {failure.merchantId}: {failure.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ---- Ledger ----------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        {['', 'PENDING', 'PROCESSING', 'PAID', 'FAILED', 'ON_HOLD'].map((value) => (
          <button
            key={value || 'ALL'}
            type="button"
            onClick={() => setStatus(value)}
            aria-pressed={status === value}
            className={`badge ${status === value ? 'badge-success' : 'badge-neutral'} cursor-pointer`}
          >
            {value ? value.replace(/_/g, ' ').toLowerCase() : 'all'}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(s) => s.settlement_ref}
        loading={isLoading}
        empty={<EmptyState title="No settlements" description="Run a batch to generate settlements for eligible merchants." />}
      />
    </div>
  );
}
